import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { accountingAPI, bankingAPI, reconciliationAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../components/ui/Table';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Landmark,
  RefreshCcw,
} from 'lucide-react';

/**
 * Loan account setup (plan §6 / §7 / §12).
 *
 * URL: /tenant/banking/accounts/:id/loan
 *
 * This page configures the loan-specific metadata attached to a
 * bank_account of type=loan_account:
 *
 *   - Lender name, loan reference
 *   - Principal, APR, term (months), start / first payment date
 *   - Payment frequency (weekly / monthly / quarterly)
 *   - Interest type (reducing balance / flat rate / interest only)
 *   - Optional default interest nominal (posted when repayments reconcile)
 *
 * As the user edits the form, a live preview of the amortisation schedule
 * is rendered via POST /reconciliation/loans/:id/preview-schedule. Saving
 * persists the details and regenerates the schedule via PUT
 * /reconciliation/loans/:id. Existing schedules can be re-amortised from
 * the next unpaid sequence without losing history.
 */

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
};

const shortDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

const todayIso = () => new Date().toISOString().slice(0, 10);

const INTEREST_TYPES = [
  { value: 'reducing_balance', label: 'Reducing balance (standard amortisation)' },
  { value: 'flat_rate', label: 'Flat rate (equal interest each period)' },
  { value: 'interest_only', label: 'Interest only (principal due at end)' },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

export default function LoanAccountSetup() {
  const { id: bankAccountId } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [existingDetails, setExistingDetails] = useState(null);
  const [existingSchedule, setExistingSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [interestAccounts, setInterestAccounts] = useState([]);

  const [form, setForm] = useState({
    lender_name: '',
    loan_reference: '',
    principal_amount: '',
    interest_rate: '',
    term_months: '',
    start_date: todayIso(),
    first_payment_date: '',
    payment_frequency: 'monthly',
    interest_type: 'reducing_balance',
    scheduled_payment_amount: '',
    interest_nominal_account_id: '',
    notes: '',
  });

  const [previewSchedule, setPreviewSchedule] = useState([]);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  const [reamortising, setReamortising] = useState(false);

  const hasReconciledLines = useMemo(
    () => existingSchedule.some((l) => l.reconciled_match_id),
    [existingSchedule]
  );

  // Initial load: account + existing loan details + schedule.
  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const [a, loanRes, nominals] = await Promise.all([
        bankingAPI.getAccount(bankAccountId),
        reconciliationAPI.getLoanAccount(bankAccountId),
        accountingAPI.getAccounts({ type: 'expense', active_only: true }).catch(() => []),
      ]);
      setAccount(a);
      setExistingDetails(loanRes?.details || null);
      setExistingSchedule(loanRes?.schedule || []);
      setInterestAccounts(Array.isArray(nominals) ? nominals : []);

      if (loanRes?.details) {
        const d = loanRes.details;
        setForm({
          lender_name: d.lender_name || '',
          loan_reference: d.loan_reference || '',
          principal_amount: String(d.principal_amount ?? ''),
          interest_rate: String(d.interest_rate ?? ''),
          term_months: String(d.term_months ?? ''),
          start_date: d.start_date ? shortDate(d.start_date) : todayIso(),
          first_payment_date: d.first_payment_date ? shortDate(d.first_payment_date) : '',
          payment_frequency: d.payment_frequency || 'monthly',
          interest_type: d.interest_type || 'reducing_balance',
          scheduled_payment_amount: d.scheduled_payment_amount
            ? String(d.scheduled_payment_amount)
            : '',
          interest_nominal_account_id: d.interest_nominal_account_id || '',
          notes: d.notes || '',
        });
      }
    } catch (err) {
      setLoadError(err?.response?.data?.error || err.message || 'Could not load loan');
    } finally {
      setLoading(false);
    }
  }, [bankAccountId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Debounced preview whenever the critical inputs change.
  useEffect(() => {
    if (
      !form.principal_amount ||
      !form.interest_rate ||
      !form.term_months ||
      !form.first_payment_date
    ) {
      setPreviewSchedule([]);
      setPreviewSummary(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setPreviewing(true);
        setPreviewError('');
        const res = await reconciliationAPI.previewLoanSchedule(bankAccountId, {
          principal_amount: Number(form.principal_amount),
          interest_rate: Number(form.interest_rate),
          term_months: Number(form.term_months),
          first_payment_date: form.first_payment_date,
          payment_frequency: form.payment_frequency,
          interest_type: form.interest_type,
          scheduled_payment_amount: form.scheduled_payment_amount
            ? Number(form.scheduled_payment_amount)
            : null,
        });
        if (cancelled) return;
        setPreviewSchedule(res.schedule || []);
        setPreviewSummary(res.summary || null);
      } catch (err) {
        if (cancelled) return;
        setPreviewError(
          err?.response?.data?.error || err.message || 'Could not build preview'
        );
        setPreviewSchedule([]);
        setPreviewSummary(null);
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    bankAccountId,
    form.principal_amount,
    form.interest_rate,
    form.term_months,
    form.first_payment_date,
    form.payment_frequency,
    form.interest_type,
    form.scheduled_payment_amount,
  ]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveOk('');
    try {
      const body = {
        lender_name: form.lender_name || null,
        loan_reference: form.loan_reference || null,
        principal_amount: Number(form.principal_amount),
        interest_rate: Number(form.interest_rate),
        term_months: Number(form.term_months),
        start_date: form.start_date,
        first_payment_date: form.first_payment_date,
        payment_frequency: form.payment_frequency,
        interest_type: form.interest_type,
        scheduled_payment_amount: form.scheduled_payment_amount
          ? Number(form.scheduled_payment_amount)
          : null,
        interest_nominal_account_id: form.interest_nominal_account_id || null,
        notes: form.notes || null,
      };
      await reconciliationAPI.upsertLoanDetails(bankAccountId, body);
      setSaveOk('Loan details saved and schedule regenerated.');
      await loadAll();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReamortise = async () => {
    if (
      !window.confirm(
        'Re-amortise the remaining schedule from the next unpaid instalment? Historical reconciled rows are preserved.'
      )
    )
      return;
    setReamortising(true);
    setSaveError('');
    setSaveOk('');
    try {
      await reconciliationAPI.reamortiseLoan(bankAccountId, {
        interest_rate: form.interest_rate ? Number(form.interest_rate) : null,
        term_months: form.term_months ? Number(form.term_months) : null,
        scheduled_payment_amount: form.scheduled_payment_amount
          ? Number(form.scheduled_payment_amount)
          : null,
      });
      setSaveOk('Schedule re-amortised from the next unpaid row.');
      await loadAll();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || 'Re-amortise failed');
    } finally {
      setReamortising(false);
    }
  };

  const disableReplace =
    hasReconciledLines && !!existingDetails && !window?.__allowOverwriteLoan;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
              <button
                type="button"
                onClick={() => navigate('/tenant/banking')}
                className="inline-flex items-center gap-1 hover:underline"
              >
                <ChevronLeft className="h-3 w-3" /> Banking
              </button>
            </div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold">
              <Landmark className="h-6 w-6" />
              Loan setup{account?.account_name ? ` — ${account.account_name}` : ''}
            </h1>
            <div className="text-xs text-gray-500">
              Configure the loan terms and amortisation schedule used by reconciliation.
            </div>
          </div>
        </div>

        {loadError && (
          <Card className="mb-4 border-red-400 bg-red-50 dark:bg-red-950/30">
            <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{loadError}</span>
            </CardContent>
          </Card>
        )}

        {!loading && account && account.account_type !== 'loan_account' && (
          <Card className="mb-4 border-amber-400 bg-amber-50 dark:bg-amber-950/30">
            <CardContent className="flex gap-2 p-3 text-xs text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                This bank account is type &quot;{account.account_type}&quot;, not
                &quot;loan_account&quot;. Loan amortisation will not be applied until the
                account type is changed.
              </span>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left: form */}
          <Card>
            <CardContent className="space-y-3 p-4">
              <div className="text-sm font-semibold">Loan terms</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FormLabel>Lender</FormLabel>
                  <Input
                    value={form.lender_name}
                    onChange={(e) => setForm((f) => ({ ...f, lender_name: e.target.value }))}
                    placeholder="e.g. Santander Business"
                  />
                </div>
                <div>
                  <FormLabel>Loan reference</FormLabel>
                  <Input
                    value={form.loan_reference}
                    onChange={(e) => setForm((f) => ({ ...f, loan_reference: e.target.value }))}
                    placeholder="e.g. BL-2026-00123"
                  />
                </div>
                <div>
                  <FormLabel>Principal (GBP)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.principal_amount}
                    onChange={(e) => setForm((f) => ({ ...f, principal_amount: e.target.value }))}
                  />
                </div>
                <div>
                  <FormLabel>APR (%)</FormLabel>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.interest_rate}
                    onChange={(e) => setForm((f) => ({ ...f, interest_rate: e.target.value }))}
                  />
                </div>
                <div>
                  <FormLabel>Term (months)</FormLabel>
                  <Input
                    type="number"
                    min="1"
                    value={form.term_months}
                    onChange={(e) => setForm((f) => ({ ...f, term_months: e.target.value }))}
                  />
                </div>
                <div>
                  <FormLabel>Payment frequency</FormLabel>
                  <Select
                    value={form.payment_frequency}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, payment_frequency: e.target.value }))
                    }
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel>Start date</FormLabel>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <FormLabel>First payment date</FormLabel>
                  <Input
                    type="date"
                    value={form.first_payment_date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, first_payment_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <FormLabel>Interest type</FormLabel>
                  <Select
                    value={form.interest_type}
                    onChange={(e) => setForm((f) => ({ ...f, interest_type: e.target.value }))}
                  >
                    {INTEREST_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel>Scheduled payment (override, optional)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Auto from amortisation"
                    value={form.scheduled_payment_amount}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, scheduled_payment_amount: e.target.value }))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <FormLabel>Default interest expense nominal</FormLabel>
                  <Select
                    value={form.interest_nominal_account_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, interest_nominal_account_id: e.target.value }))
                    }
                  >
                    <option value="">—</option>
                    {interestAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.accountNumber ? `${a.accountNumber} — ` : ''}
                        {a.accountName || a.account_name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="col-span-2">
                  <FormLabel>Notes</FormLabel>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              {saveError && (
                <div className="flex items-start gap-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{saveError}</span>
                </div>
              )}
              {saveOk && (
                <div className="flex items-start gap-2 rounded border border-green-300 bg-green-50 p-2 text-xs text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{saveOk}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving || disableReplace}>
                  {saving ? 'Saving…' : existingDetails ? 'Save + regenerate schedule' : 'Create loan + generate schedule'}
                </Button>
                {existingDetails && hasReconciledLines && (
                  <Button
                    variant="outline"
                    onClick={handleReamortise}
                    disabled={reamortising}
                  >
                    <RefreshCcw className="mr-1 h-3.5 w-3.5" />
                    {reamortising ? 'Re-amortising…' : 'Re-amortise remaining'}
                  </Button>
                )}
              </div>

              {hasReconciledLines && existingDetails && (
                <div className="text-[11px] text-amber-700 dark:text-amber-300">
                  This loan already has reconciled repayments. &quot;Save + regenerate&quot;
                  is disabled to protect history — use &quot;Re-amortise remaining&quot; to
                  adjust the ongoing terms without rewriting the past.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: live schedule preview */}
          <Card>
            <CardContent className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold">
                  {existingSchedule.length > 0 ? 'Current schedule' : 'Schedule preview'}
                </div>
                {previewSummary && (
                  <div className="text-[11px] text-gray-500">
                    {previewSummary.periods} periods · total interest{' '}
                    {formatMoney(previewSummary.total_interest)} · total paid{' '}
                    {formatMoney(previewSummary.total_payment)}
                  </div>
                )}
              </div>

              {previewError && (
                <div className="mb-2 flex items-start gap-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{previewError}</span>
                </div>
              )}

              <div className="max-h-[520px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead align="right">Payment</TableHead>
                      <TableHead align="right">Principal</TableHead>
                      <TableHead align="right">Interest</TableHead>
                      <TableHead align="right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(existingSchedule.length > 0 ? existingSchedule : previewSchedule).map(
                      (l, idx) => (
                        <TableRow key={l.id || `preview-${idx}`}>
                          <TableCell>{l.sequence}</TableCell>
                          <TableCell>{shortDate(l.due_date)}</TableCell>
                          <TableCell align="right">{formatMoney(l.scheduled_payment)}</TableCell>
                          <TableCell align="right">{formatMoney(l.principal_portion)}</TableCell>
                          <TableCell align="right">{formatMoney(l.interest_portion)}</TableCell>
                          <TableCell align="right">{formatMoney(l.balance_after)}</TableCell>
                          <TableCell>
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">
                              {l.status || (existingSchedule.length === 0 ? 'preview' : '—')}
                            </span>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>

              {previewing && (
                <div className="mt-2 text-[11px] text-gray-500">Rebuilding preview…</div>
              )}
              {!previewing &&
                previewSchedule.length === 0 &&
                existingSchedule.length === 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Fill in principal, APR, term and first payment date to preview the
                    schedule.
                  </div>
                )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
